import { LoadingButton } from "@mui/lab";
import {
  Checkbox,
  Stack,
  useTheme,
  Button,
  Typography,
  Divider,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { useCreateDocumentRule, useUpdateDocumentRule } from "hooks/api/useDocumentRules";
import { BookOpenText, Plus } from "@phosphor-icons/react";
import { ANTooltip } from "@answersnow/pkg-theme";
import React from "react";
import PlanDetailsTab from "./PlanDetailsTab";
import CptMappingTab from "./CptMappingTab";
import DocumentRulesDialog from "../DocumentRules/DocumentRulesDialog";
import DocumentRuleCard from "../DocumentRules/DocumentRuleCard";
import { CLINICAL_DOCUMENT_TYPE_OPTIONS } from "../DocumentRules/constants";
import actions from "actions";
import { useDispatch, useSelector } from "react-redux";
import * as selectors from "selectors";

export default function InsurancePlanEdit({
  onCancel,
  isLoading,
  disableSaveButton,
  titlePanel,
  onChange,
  updateDataPanel,
  dataPanel,
  onRulesChanged,
}: {
  onCancel: () => void;
  isLoading: boolean;
  disableSaveButton: boolean;
  titlePanel: string;
  onChange: (
    name: string,
    onChangeType: any,
    data: any
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  updateDataPanel: (key: string, value: any) => void;
  dataPanel: any;
  onRulesChanged?: () => void;
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const insuranceProvidersList = useSelector(selectors.getInsuranceProvidersList);
  const isCreating = !dataPanel.id;
  const maxDocumentRules = CLINICAL_DOCUMENT_TYPE_OPTIONS.length;
  const hasReachedMaxDocumentRules = (dataPanel.document_rules ?? []).length >= maxDocumentRules;

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState<any>(null);

  const { createDocumentRuleMutation, isCreateDocumentRuleLoading } = useCreateDocumentRule({
    onSuccess: (response: any) => {
      setDialogOpen(false);
      setEditingRule(null);
      const newRule = response?.data?.data ?? response?.data;
      if (newRule?.id) {
        updateDataPanel("document_rules", [...(dataPanel.document_rules ?? []), newRule]);
      }
      onRulesChanged?.();
    },
  });

  const { updateDocumentRuleMutation, isUpdateDocumentRuleLoading } = useUpdateDocumentRule({
    onSuccess: (response: any, variables: any) => {
      setDialogOpen(false);
      setEditingRule(null);
      const updatedRule = response?.data?.data ?? response?.data;
      if (updatedRule?.id) {
        updateDataPanel(
          "document_rules",
          (dataPanel.document_rules ?? []).map((r: any) =>
            r.id === updatedRule.id ? updatedRule : r
          )
        );
      } else {
        // Fallback: apply the payload we sent to the existing rule
        updateDataPanel(
          "document_rules",
          (dataPanel.document_rules ?? []).map((r: any) =>
            r.id === variables.ruleId ? { ...r, ...variables.payload } : r
          )
        );
      }
      onRulesChanged?.();
    },
  });

  const handleOpenAddRule = () => {
    setEditingRule(null);
    setDialogOpen(true);
  };

  const handleSaveRule = (rule: any) => {
    const payload = {
      clinical_document_type: rule.clinical_document_type,
      required_assessments: rule.required_assessments,
      requires_referring_provider: rule.requires_referring_provider,
      requires_criminal_legal_history: rule.requires_criminal_legal_history,
      requires_drug_alcohol_use: rule.requires_drug_alcohol_use,
      requires_trauma_history: rule.requires_trauma_history,
    };
    if (rule.id) {
      updateDocumentRuleMutation({ planId: dataPanel.id, ruleId: rule.id, payload });
    } else {
      createDocumentRuleMutation({ planId: dataPanel.id, payload });
    }
  };

  return (
    <Stack p={7} gap={7}>
      <Stack flexDirection="row" gap={3} alignItems="center">
        <BookOpenText fontSize="20px" weight="duotone" color={theme.palette.primary.main} />
        <ANTooltip component={Typography} variant="subtitle1" style={{ maxWidth: "90%" }}>
          {isCreating ? "Add Insurance Plan" : titlePanel}
        </ANTooltip>
      </Stack>
      <Stack gap={5}>
        <PlanDetailsTab
          onChange={onChange}
          data={dataPanel}
          providers={insuranceProvidersList}
          errors={[]}
        />

        {!isCreating && (
          <>
            <Divider />
            <Stack gap="16px">
              <Stack>
                <Typography variant="subtitle2">Clinical document rules</Typography>
                <Typography variant="caption" color="text.secondary">
                  Assessments that must be current before a document can be submitted. Each
                  assessment must have been administered within the last 60 days.
                </Typography>
              </Stack>

              {(dataPanel.document_rules ?? []).map((rule: any) => (
                <DocumentRuleCard
                  key={rule.id}
                  rule={rule}
                  action={
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setEditingRule(rule);
                        setDialogOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  }
                />
              ))}

              <Tooltip
                title={hasReachedMaxDocumentRules ? "No additional document types available" : ""}
              >
                <span>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Plus size={16} />}
                    onClick={handleOpenAddRule}
                    disabled={hasReachedMaxDocumentRules}
                  >
                    Add Document Rules
                  </Button>
                </span>
              </Tooltip>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={dataPanel.requires_sdoh_survey ?? false}
                    onChange={onChange("requires_sdoh_survey", undefined, dataPanel)}
                  />
                }
                label="Plan requires Social Determinant of Health Survey"
              />
            </Stack>
            <DocumentRulesDialog
              open={dialogOpen}
              onClose={() => {
                setDialogOpen(false);
                setEditingRule(null);
              }}
              onSave={handleSaveRule}
              existingRule={editingRule}
              existingTypes={
                dataPanel.document_rules?.map((r: any) => r.clinical_document_type) ?? []
              }
              loading={isCreateDocumentRuleLoading || isUpdateDocumentRuleLoading}
            />
          </>
        )}

        <Divider />
        <CptMappingTab onChange={onChange} data={dataPanel} />

        <Stack gap={3}>
          <LoadingButton
            disabled={disableSaveButton}
            onClick={() => {
              if (isCreating) dispatch(actions.createInsurancePlan(dataPanel));
              else dispatch(actions.updateInsurancePlan(dataPanel));
            }}
            loading={isLoading}
          >
            {isCreating ? "Add Insurance Plan" : "Save Changes"}
          </LoadingButton>
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
